import React from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { Shield, Eye, Lock, Users, FileText, Scale, Mail, Phone, MapPin } from 'lucide-react';

const PrivacyPolicy: React.FC = () => {
  const sections = [
    {
      id: "information-we-collect",
      title: "1. Information We Collect",
      icon: <Eye className="w-6 h-6" />,
      content: [
        "The Company may collect the following types of information:",
        "Personal Information: Name, email address, phone number, company name, or other details submitted through forms or inquiries.",
        "Usage Data: Information about how you use the Website, including IP address, browser type, pages visited, and time spent on the Website.",
        "Cookies and Tracking Technologies: The Website may use cookies, web beacons, and similar technologies to improve user experience and analyze Website performance."
      ]
    },
    {
      id: "how-we-use-information",
      title: "2. How We Use Your Information",
      icon: <FileText className="w-6 h-6" />,
      content: [
        "The Company may use the information collected for the following purposes:",
        "To provide and improve the Website and our services.",
        "To respond to inquiries, requests, or customer support needs.",
        "To send important updates, newsletters, or promotional content (if you have opted in).",
        "To monitor and analyze Website usage for security, performance, and optimization.",
        "To comply with legal obligations."
      ]
    },
    {
      id: "sharing-of-information",
      title: "3. Sharing of Information",
      icon: <Users className="w-6 h-6" />,
      content: [
        "The Company does not sell, rent, or trade your personal information. Information may only be shared in the following circumstances:",
        "Service Providers: With trusted third parties who assist in operating the Website or delivering services, subject to confidentiality obligations.",
        "Legal Requirements: When disclosure is required to comply with applicable laws, regulations, or legal processes.",
        "Business Transfers: In connection with a merger, acquisition, or sale of assets, personal information may be transferred as part of the transaction."
      ]
    },
    {
      id: "data-security",
      title: "4. Data Security",
      icon: <Lock className="w-6 h-6" />,
      content: [
        "The Company uses reasonable administrative, technical, and physical safeguards to protect your personal information.",
        "However, no method of transmission over the internet or electronic storage is 100% secure, and the Company cannot guarantee absolute security."
      ]
    },
    {
      id: "your-rights",
      title: "5. Your Rights",
      icon: <Scale className="w-6 h-6" />,
      content: [
        "Depending on applicable laws, you may have the right to:",
        "Access, correct, or update your personal information.",
        "Request deletion of your personal information.",
        "Opt out of receiving promotional communications.",
        "Withdraw consent to the use of cookies or tracking technologies.",
        "Requests can be made by contacting the Company (see Contact Information below)."
      ]
    },
    {
      id: "third-party-links",
      title: "6. Third-Party Links",
      icon: <FileText className="w-6 h-6" />,
      content: [
        "The Website may contain links to third-party websites.",
        "The Company is not responsible for the privacy practices or content of external websites.",
        "Users are encouraged to review the privacy policies of any third-party sites visited."
      ]
    },
    {
      id: "childrens-privacy",
      title: "7. Children's Privacy",
      icon: <Shield className="w-6 h-6" />,
      content: [
        "The Website is not intended for children under the age of 13.",
        "The Company does not knowingly collect personal information from children.",
        "If it is discovered that personal data of a child has been collected, the Company will take steps to delete such information promptly."
      ]
    },
    {
      id: "changes-to-policy",
      title: "8. Changes to This Privacy Policy",
      icon: <FileText className="w-6 h-6" />,
      content: [
        "The Company reserves the right to update or modify this Privacy Policy at any time.",
        "Any changes will be posted on this page with an updated \"Last Updated\" date.",
        "Continued use of the Website after such modifications constitutes acceptance of the revised Privacy Policy."
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      <SEOHead 
        title="Privacy Policy - Data Protection & Privacy Rights"
        description="CRMONCE Privacy Policy explains how we collect, use, and protect your personal data. Learn about your privacy rights and our commitment to data protection."
        keywords="privacy policy, data protection, privacy rights, GDPR compliance, data security, personal information"
        canonical="/privacy-policy"
      />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-r from-green-600 to-blue-600 text-white" style={{ marginTop: '80px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex items-center justify-center mb-6">
              <Shield className="w-12 h-12 mr-4" />
              <h1 className="text-5xl font-bold">Privacy Policy</h1>
            </div>
            <p className="text-xl text-green-100 mb-4">
              Last Updated: September 18, 2025
            </p>
            <p className="text-lg text-green-100">
              CRMONCE (OPC) Pvt Ltd values your privacy and is committed to protecting your personal data. 
              This Privacy Policy explains how the Company collects, uses, discloses, and safeguards information 
              when you visit crmonce.com (the "Website").
            </p>
          </motion.div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-8 lg:p-12 mb-8"
          >
            <div className="flex items-center mb-6">
              <Shield className="w-6 h-6 text-green-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Your Privacy Matters</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              By using the Website, you agree to the practices described in this Privacy Policy. 
              We are committed to transparency in how we handle your personal information and 
              protecting your privacy rights.
            </p>
          </motion.div>

          {/* Privacy Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * (index + 1) }}
                className="bg-white rounded-xl shadow-lg p-8"
              >
                <div className="flex items-center mb-4">
                  <div className="text-green-600 mr-3">
                    {section.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {section.title}
                  </h3>
                </div>
                <div className="space-y-3">
                  {section.content.map((paragraph, pIndex) => (
                    <p key={pIndex} className="text-gray-700 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl shadow-lg p-8 mt-12"
          >
            <div className="flex items-center mb-6">
              <Scale className="w-6 h-6 mr-3" />
              <h3 className="text-2xl font-bold">9. Contact Information</h3>
            </div>
            <p className="text-green-100 mb-6">
              For any questions or concerns regarding this Privacy Policy, please contact the Company at:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-lg mb-3">CRMONCE (OPC) Pvt Ltd</h4>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 mr-3 text-green-200" />
                    <a href="mailto:info@crmonce.com" className="text-green-100 hover:text-white transition-colors">
                      info@crmonce.com
                    </a>
                  </div>
                  <div className="flex items-center">
                    <Phone className="w-5 h-5 mr-3 text-green-200" />
                    <a href="tel:+918096556344" className="text-green-100 hover:text-white transition-colors">
                      +91 80965 56344
                    </a>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-lg mb-3">Address</h4>
                <div className="flex items-start">
                  <MapPin className="w-5 h-5 mr-3 text-green-200 mt-1 flex-shrink-0" />
                  <div className="text-green-100">
                    <p>8-112, Gamallapalem, Kodurupadu</p>
                    <p>Nellore, AP-524314, India</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Data Protection Notice */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="bg-gray-50 rounded-xl p-6 mt-8"
          >
            <div className="text-center">
              <Shield className="w-8 h-8 text-green-600 mx-auto mb-3" />
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Data Protection Commitment</h4>
              <p className="text-sm text-gray-600">
                This policy covers data collection, use, sharing, security, rights, and compliance—the essentials 
                for a corporate website like crmonce.com. We are committed to maintaining the highest standards 
                of data protection and privacy for all our users.
              </p>
            </div>
          </motion.div>

          {/* Legal Notice */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="bg-blue-50 border border-blue-200 rounded-xl p-6 mt-6 text-center"
          >
            <p className="text-sm text-blue-800">
              <strong>Important:</strong> This Privacy Policy should be read in conjunction with our Terms and Conditions. 
              If you have any questions about how we handle your personal data or wish to exercise your privacy rights, 
              please don't hesitate to contact us using the information provided above.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
